import { motion } from "framer-motion";
import { Radar, Search, SlidersHorizontal, Bell } from "lucide-react";
import { EXCEPTIONS } from "@/data/exceptionHub";
import { PriorityBadge, StatusPill, Progress, Avatar } from "./primitives";
import { EASE } from "@/lib/motion";

const rowVariant = {
  hidden: { opacity: 0, y: 12 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE, delay: 0.5 + i * 0.09 } }),
};

export const ExceptionDashboard = () => {
  const highCount = EXCEPTIONS.filter((e) => e.priority === "high").length;
  const resolved = EXCEPTIONS.filter((e) => e.progress === 100).length;

  return (
    <div data-testid="exception-dashboard-visual" className="relative w-full rounded-4xl bg-white border border-slate-100 shadow-float overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-white/80 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-8 w-8 rounded-xl bg-navy text-white"><Radar className="h-4 w-4" strokeWidth={1.75} /></span>
          <div className="leading-tight">
            <p className="text-sm font-semibold text-navy font-heading">Exception Hub™</p>
            <p className="text-[11px] text-ink-400">Operations Command Center</p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-ink-300">
          <span className="grid place-items-center h-8 w-8 rounded-lg hover:bg-slate-50 transition-colors"><Search className="h-4 w-4" strokeWidth={1.75} /></span>
          <span className="grid place-items-center h-8 w-8 rounded-lg hover:bg-slate-50 transition-colors"><SlidersHorizontal className="h-4 w-4" strokeWidth={1.75} /></span>
          <span className="relative grid place-items-center h-8 w-8 rounded-lg hover:bg-slate-50 transition-colors">
            <Bell className="h-4 w-4" strokeWidth={1.75} />
            <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-priority-text ring-2 ring-white" />
          </span>
        </div>
      </div>

      <div className="grid grid-cols-3 divide-x divide-slate-100 border-b border-slate-100">
        {[
          { k: "Open exceptions", v: EXCEPTIONS.length, accent: "text-navy" },
          { k: "High priority", v: highCount, accent: "text-priority-text" },
          { k: "Resolved today", v: resolved, accent: "text-done-text" },
        ].map((s) => (
          <div key={s.k} className="px-5 py-4">
            <p className={`text-2xl font-bold font-heading tracking-tight ${s.accent}`}>{s.v}</p>
            <p className="text-[11px] text-ink-400 mt-0.5">{s.k}</p>
          </div>
        ))}
      </div>

      <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 bg-canvas-soft">
        <span className="col-span-5 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-400">Exception</span>
        <span className="col-span-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-400">Priority</span>
        <span className="col-span-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-400">Owner</span>
        <span className="col-span-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-400">Progress</span>
      </div>

      <div className="divide-y divide-slate-50">
        {EXCEPTIONS.map((row, i) => (
          <motion.div key={row.id} custom={i} variants={rowVariant} initial="hidden" animate="show"
            className="grid grid-cols-12 gap-3 px-5 py-3.5 items-center hover:bg-slate-50/60 transition-colors">
            <div className="col-span-12 md:col-span-5 flex items-center gap-3 min-w-0">
              <span className="font-mono text-[10px] text-ink-300 hidden lg:block">{row.id}</span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-navy truncate">{row.title}</p>
                <div className="md:hidden mt-1"><StatusPill status={row.status} done={row.progress === 100} /></div>
              </div>
            </div>
            <div className="col-span-6 md:col-span-2"><PriorityBadge priority={row.priority} /></div>
            <div className="hidden md:flex col-span-2 items-center gap-2">
              <Avatar src={row.avatar} alt={row.owner} size="h-6 w-6" />
              <span className="text-xs text-ink-500 truncate">{row.owner.split(" ")[0]}</span>
            </div>
            <div className="col-span-6 md:col-span-3 flex items-center gap-3">
              <Progress value={row.progress} done={row.progress === 100} />
              <span className="text-[11px] text-ink-400 tabular-nums w-8 text-right">{row.progress}%</span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
